import React, { useState, useEffect, useRef } from "react";
import { Modal, Box, Typography, Divider } from "@mui/material";
import Grid from "@mui/system/Grid";
import { useMsal } from "@azure/msal-react";

import {
  ButtonVerifyStyle,
  HorizontalDivider,
  MfaModalBox,
  ModalGrid,
} from "../styles/styles";
import { ButtonContained } from "./button";
import { flexAllCentre } from "../../styles/mui/styles/display";
import { IDLE_TIMEOUT, SESSION_TIMEOUT } from "../../constants/HomePage";
import { Numbers } from "../../constants/Referral";

interface SessionTimeoutProps {
  logoutPortalApp: () => void;
}

const SessionTimeout: React.FC<SessionTimeoutProps> = ({ logoutPortalApp }) => {
  const [isIdle, setIsIdle] = useState(false);
  const [countdown, setCountdown] = useState(IDLE_TIMEOUT / 1000);
  const { instance } = useMsal();

  const idleTimeout = useRef<NodeJS.Timeout | null>(null);
  const countdownInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const isLoggingOut = useRef<boolean>(false);

  const resetIdleTimer = () => {
    if (idleTimeout.current) {
      clearTimeout(idleTimeout.current);
    }

    idleTimeout.current = setTimeout(() => {
      setIsIdle(true);
      setCountdown(IDLE_TIMEOUT / 1000);
    }, SESSION_TIMEOUT);
  };

  useEffect(() => {
    if (!isIdle) return;

    countdownInterval.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= Numbers.ONE) {
          return Numbers.ZERO;
        }
        return prev - Numbers.ONE;
      });
    }, 1000);

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [isIdle]);

  useEffect(() => {
    if (isIdle && countdown <= Numbers.ZERO && !isLoggingOut.current) {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
      isLoggingOut.current = true;

      const performLogout = async () => {
        try {
          logoutPortalApp();
        } catch (error) {
          isLoggingOut.current = false;
        }
      };

      performLogout();
    }
  }, [countdown, isIdle, instance]);

  const handleOk = () => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
    }
    if (idleTimeout.current) {
      clearTimeout(idleTimeout.current);
    }

    setIsIdle(false);
    setCountdown(IDLE_TIMEOUT / 1000);
    isLoggingOut.current = false;
  };

  useEffect(() => {
    const handleActivity = () => {
      if (!isIdle) {
        resetIdleTimer();
      }
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity);

    if (!isIdle) {
      resetIdleTimer();
    }

    return () => {
      if (idleTimeout.current) {
        clearTimeout(idleTimeout.current);
      }
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity);
    };
  }, [isIdle]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' + remainingSeconds : remainingSeconds}`;
  };

  return (
    <Modal
      open={isIdle}
      onClose={() => {}}
      aria-labelledby="session-timeout-modal"
      aria-describedby="session-timeout-modal-description"
      sx={flexAllCentre}
    >
      <Box sx={MfaModalBox}>
        <Grid container mb="0.4rem">
          <Grid size={{ xs: 11.5 }}>
            <Typography id="modal-modal-title" variant="h1">
              Your session will expire in {formatTime(countdown)}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={HorizontalDivider} />
        <Box pt={3} pb={3} sx={flexAllCentre}>
          <Typography id="modal-modal-title" variant="h1" component="h2">
            Hit Ok to stay logged in!
          </Typography>
        </Box>
        <Divider sx={HorizontalDivider} />
        <Grid container sx={ModalGrid}>
          <Grid size={{ xs: 12 }} sx={flexAllCentre}>
            <ButtonContained
              id="btnVerify"
              onClick={handleOk}
              label="Ok"
              style={ButtonVerifyStyle}
            />
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default SessionTimeout;
